import {
  HrmRequestFormResponse,
  ITRequestFormResponse,
  MaterialRequisitionFormResponse,
  PurchaseFormResponse,
  ResignationRequestFormResponse,
} from '@Types/api/form';
import { RequestType } from '@Enum/index';
import useStatusFinderByNum from '@Hooks/useStatusFinderByNum';
import { RequestTypes } from '@Types/index';
import { parseDateTime } from '@Utils/transformDateTime';
import { useAppSelector, useLeaveTypeTitleFinderById } from '@Hooks/index';
import { getResumptionWorkShiftDict } from '@Store/selectors/dictionarySelector';

interface MapperValue {
  requestType: string;
  status: string;
  items: Array<{ label: string; value: string }>;
}

const mapper: MapperValue = {
  requestType: '',
  status: '',
  items: [
    { label: 'From', value: '' },
    { label: 'To', value: '' },
    { label: 'Total', value: '' },
  ],
};

const useSmartMapper = (
  type: RequestTypes
): {
  transformItem: (
    item:
      | HrmRequestFormResponse
      | ITRequestFormResponse
      | ResignationRequestFormResponse
      | MaterialRequisitionFormResponse
      | PurchaseFormResponse
  ) => MapperValue;
} => {
  const resumptionWorkShifts = useAppSelector(getResumptionWorkShiftDict);
  const { findStatus } = useStatusFinderByNum(type);
  const { findRequestTitle } = useLeaveTypeTitleFinderById(type);

  const transformItem = (
    item:
      | HrmRequestFormResponse
      | ITRequestFormResponse
      | ResignationRequestFormResponse
      | MaterialRequisitionFormResponse
      | PurchaseFormResponse
  ): MapperValue => {
    const {
      leaveTypeID,
      reqStatusId,
      startDate,
      endDate,
      totDays,
      ticketRequired,
      travelDate,
      returnDate,
    } = item as HrmRequestFormResponse;
    const { reqItemId, empNumber, createdDate, username, statusId } =
      item as ITRequestFormResponse;
    const {
      reason,
      empNumber: resignationEmpNumber,
      resDate,
      lastWrkngDate,
    } = item as ResignationRequestFormResponse;
    const {
      reqNo,
      statusId: materialStatus,
      reqDate,
      createdOn,
    } = item as MaterialRequisitionFormResponse;
    const {
      reqNo: purchaseReqNo,
      requiredOn,
      statusId: purchaseStatus,
      reqDate: purchaseReqDate,
      reqFromId: purchaseFrom,
    } = item as PurchaseFormResponse;
    switch (type) {
      case RequestType.LEAVE:
      case RequestType.PASSPORT:
      case RequestType.SALARY:
      case RequestType.ATTENDANCE:
        return {
          requestType: findRequestTitle(leaveTypeID),
          status: findStatus(reqStatusId)?.codeDesc || '',
          items: [
            { label: 'From', value: parseDateTime(startDate) },
            {
              label: 'To',
              value: parseDateTime(endDate),
            },
            { label: 'Total', value: String(totDays || '') },
          ],
        };
      case RequestType.RESUMPTION:
        return {
          requestType: findRequestTitle(leaveTypeID),
          status: findStatus(reqStatusId)?.codeDesc || '',
          items: [
            { label: 'From', value: parseDateTime(startDate) },
            {
              label: 'To',
              value: parseDateTime(endDate),
            },
            {
              label: 'Work Shift',
              value: resumptionWorkShifts.find(
                (workShift) => workShift.codeNum === ticketRequired
              )!.codeDesc,
            },
          ],
        };
      case RequestType.OVERSEAS:
        return {
          requestType: findRequestTitle(leaveTypeID),
          status: findStatus(reqStatusId)?.codeDesc || '',
          items: [
            { label: 'From', value: parseDateTime(travelDate) },
            {
              label: 'To',
              value: parseDateTime(returnDate),
            },
            {
              label: 'Total',
              value: String(totDays || ''),
            },
          ],
        };
      case RequestType.IT_REQUISITION:
        return {
          requestType: findRequestTitle(reqItemId),
          status: findStatus(statusId)?.codeDesc || '',
          items: [
            { label: 'To', value: username || '' },
            {
              label: 'Employee ID',
              value: empNumber,
            },
            {
              label: 'Join Date',
              value: parseDateTime(createdDate),
            },
          ],
        };
      case RequestType.RESIGNATION:
        return {
          requestType: findRequestTitle(reason),
          status: findStatus(reqStatusId)?.codeDesc || '',
          items: [
            { label: 'Employee ID', value: resignationEmpNumber },
            {
              label: 'Resignation Date',
              value: parseDateTime(resDate),
            },
            {
              label: 'Last Day',
              value: parseDateTime(lastWrkngDate),
            },
          ],
        };
      case RequestType.MATERIAL:
        return {
          requestType: reqNo,
          status: findStatus(materialStatus)?.codeDesc || '',
          items: [
            { label: 'Request Date', value: parseDateTime(reqDate) },
            {
              label: 'Created On',
              value: parseDateTime(createdOn),
            },
            {
              label: 'Request Type',
              value: 'Material',
            },
          ],
        };
      case RequestType.PURCHASE:
        return {
          requestType: String(purchaseReqNo),
          status: findStatus(purchaseStatus)?.codeDesc || '',
          items: [
            { label: 'Requested On', value: parseDateTime(purchaseReqDate) },
            {
              label: 'Required On',
              value: parseDateTime(requiredOn),
            },
            {
              label: 'From',
              value: purchaseFrom === 1 ? 'Buildsmart' : 'TBMS 2.0',
            },
          ],
        };
      default:
        return mapper;
    }
  };

  return { transformItem };
};

export default useSmartMapper;
